import type { OutputOf } from '@going-once/client';
import { cssVar } from '@going-once/tokens';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Box, Card, Pressable, Stack, Text } from '../ui/primitives';
import { ErrorNote, Loading } from '../ui/state';
import { formatRemaining } from './Auction';
/** Moved to `@going-once/client`; re-exported because several screens import it from here. */
export { formatCents } from '@going-once/client';
import { formatCents, retryOnce } from '@going-once/client';

type FeedCard = OutputOf<'discovery.listing.browse'>['listings'][number];
type AuctionCard = OutputOf<'auctions.auction.browse'>['auctions'][number];

/**
 * The guest feed — the one screen a stranger sees, and the only one that needs no token.
 *
 * It renders `discovery.listing.browse`, Discovery's projection in OpenSearch (D63). The first
 * read after a quiet spell can find the collection asleep and be refused as retryable while it
 * wakes, so the feed asks once more before showing an error.
 */
export function Feed({
  onOpenSeller,
  onOpenAuction,
  onOpenShow,
}: {
  onOpenSeller: () => void;
  onOpenAuction: (auctionId: string) => void;
  onOpenShow: (showId: string) => void;
}) {
  const feed = useAsync(() => retryOnce(() => api().query('discovery.listing.browse', { limit: 24 })), []);
  const ending = useAsync(() => api().query('auctions.auction.browse', { limit: 12 }), []);
  const shows = useAsync(() => api().query('shows.show.browse', { limit: 12 }), []);

  return (
    <Stack gap="lg">
      {/*
        Live first, because a show is the only thing here that is happening *now* and
        stops happening if nobody arrives.

        **S-A5 as far as the data allows.** The artboard's live card is an image tile with
        a pink `● LIVE` pill, a watching count and the seller's avatar. Two of those do not
        exist: shows have no thumbnail, and presence is deliberately unbuilt (D2 says it
        must come from AppSync connections, never IVS participants — a count taken from
        the stage is wrong by default). So the tile is the gradient the hero uses when
        there is no photo, the pill is real, and the watching count is **absent rather
        than invented** — a board demo is exactly where a number nobody computes gets
        asked about.
      */}
      {shows.data && shows.data.shows.length > 0 ? (
        <Stack gap="sm">
          <Text as="h2" step="display">
            Live &amp; upcoming
          </Text>
          {shows.data.shows.map((show) => (
            <ShowCard key={show.showId} show={show} onOpen={() => onOpenShow(show.showId)} />
          ))}
        </Stack>
      ) : null}

      {/* Ending soon is *the* discovery path for timed auctions (D13 addendum): with no
          show to find them by, "what is about to end" is what replaces the lineup. */}
      <Stack gap="sm">
        <Text as="h2" step="heading">
          Ending soon
        </Text>

        {/* Rendered even when it fails or comes back empty. A section that simply is not
            there when the query 404s is indistinguishable from one nobody built, which
            is exactly how this looked the first time it was pointed at an API deployed
            before the auctions capabilities existed. */}
        {ending.error ? <ErrorNote error={ending.error} onRetry={ending.reload} /> : null}

        {ending.data && ending.data.auctions.length === 0 ? (
          <Text tone="fg.muted">
            No auctions running. A seller opens one from the Sell tab, on any published listing priced for auction.
          </Text>
        ) : null}

        {ending.data && ending.data.auctions.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', // token-exempt: grid track sizing, not a design value
              gap: 'var(--go-space-md)',
            }}
          >
            {ending.data.auctions.map((auction) => (
              <AuctionCardTile
                key={auction.auctionId}
                auction={auction}
                onOpen={() => onOpenAuction(auction.auctionId)}
              />
            ))}
          </div>
        ) : null}
      </Stack>

      <Stack direction="row" justify="between" align="center">
        <Text as="h1" step="display">
          Live now
        </Text>
        <Text step="label" tone="fg.muted">
          {feed.data ? `${feed.data.listings.length} listings` : ''}
        </Text>
      </Stack>

      {feed.loading ? <Loading what="the feed" /> : null}
      {feed.error ? <ErrorNote error={feed.error} onRetry={feed.reload} /> : null}

      {feed.data && feed.data.listings.length === 0 ? (
        <Card>
          <Stack gap="sm" align="start">
            <Text step="heading">Nothing published yet</Text>
            <Text tone="fg.muted">
              A listing appears here the moment a seller publishes it. Create one to see the whole path — photo, AI
              suggestions, publish.
            </Text>
            <button type="button" onClick={onOpenSeller} style={{ all: 'unset', cursor: 'pointer' }}>
              <Text tone="fg.muted">
                <u>Start a listing</u>
              </Text>
            </button>
          </Stack>
        </Card>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', // token-exempt: grid track sizing, not a design value
          gap: 'var(--go-space-md)',
        }}
      >
        {(feed.data?.listings ?? []).map((listing) => (
          <ListingCard key={listing.listingId} listing={listing} />
        ))}
      </div>
    </Stack>
  );
}

function ListingCard({ listing }: { listing: FeedCard }) {
  const price = listing.startPriceCents ?? listing.fixedPriceCents;
  return (
    <Card padding="sm">
      <Stack gap="sm">
        <Box background="bg.sunken" radius="md" style={{ aspectRatio: '1 / 1', overflow: 'hidden' }}>
          {listing.coverMediaKey ? (
            <img
              src={api().mediaUrl(listing.coverMediaKey)}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Stack align="center" justify="center" style={{ height: '100%' }}>
              <Text step="label" tone="fg.muted">
                No photo
              </Text>
            </Stack>
          )}
        </Box>
        <Text truncate>{listing.title}</Text>
        <Stack direction="row" justify="between" align="center">
          <Text step="label" tone="fg.muted">
            {listing.condition ?? '—'}
          </Text>
          {/* Integer cents everywhere, formatted only at the edge of the system. */}
          <Text step="label">{price === undefined ? '—' : formatCents(price)}</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

function AuctionCardTile({ auction, onOpen }: { auction: AuctionCard; onOpen: () => void }) {
  return (
    <Card padding="sm">
      <Stack gap="sm">
        <Box background="bg.sunken" radius="md" style={{ aspectRatio: '1 / 1', overflow: 'hidden' }}>
          {auction.mediaKeys[0] ? (
            <img
              src={api().mediaUrl(auction.mediaKeys[0])}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Stack align="center" justify="center" style={{ height: '100%' }}>
              <Text step="label" tone="fg.muted">
                No photo
              </Text>
            </Stack>
          )}
        </Box>
        <Text truncate>{auction.title}</Text>
        <Stack direction="row" justify="between" align="center">
          <Text step="label" tone="danger">
            {formatCents(auction.currentPriceCents)}
          </Text>
          <Text step="label" tone="success">
            {formatRemaining(auction.endTimeMs - auction.serverNowMs)}
          </Text>
        </Stack>
        <Pressable variant="secondary" onPress={onOpen}>
          {auction.bidCount === 0 ? 'Bid first' : `${auction.bidCount} bids`}
        </Pressable>
      </Stack>
    </Card>
  );
}

/**
 * A show in the feed — the tile from frame 2, minus the two things we cannot honestly
 * draw (see the note at the call site).
 *
 * It is a whole tappable tile rather than a row with a button, because that is what the
 * artboard shows and because on a phone the card *is* the target.
 */
function ShowCard({ show, onOpen }: { show: OutputOf<'shows.show.browse'>['shows'][number]; onOpen: () => void }) {
  const live = show.state === 'live';
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        appearance: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        background: 'transparent',
        borderRadius: cssVar('radius.lg'),
      }}
    >
      <Box
        radius="lg"
        style={{
          position: 'relative',
          aspectRatio: '16 / 9', // token-exempt: frame proportion, not a design value
          overflow: 'hidden',
          background: cssVar('gradient.hero'),
        }}
      >
        {/* The first lot's photo. A show card with nothing in it is a large empty
            rectangle, and the gradient was carrying a frame it was only ever meant to
            back. */}
        {show.coverMediaKey ? (
          <img
            src={api().mediaUrl(show.coverMediaKey)}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : null}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to bottom, ${cssVar('color.overlay.scrim')}, transparent 45%, ${cssVar('color.overlay.scrim')})`,
          }}
        />
        <div style={{ position: 'absolute', top: cssVar('space.md'), left: cssVar('space.md') }}>
          {live ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: cssVar('space.xs'),
                padding: `${cssVar('space.xs')} ${cssVar('space.sm')}`,
                borderRadius: cssVar('radius.full'),
                background: cssVar('color.live'),
                color: cssVar('color.fg.inverse'),
                fontFamily: cssVar('family.base'),
                fontSize: cssVar('type.label.size'),
                fontWeight: cssVar('type.label.weight'),
                letterSpacing: '0.08em', // token-exempt: tracking has no slot; flagged for design
              }}
            >
              <span
                style={{
                  width: cssVar('space.xs'),
                  height: cssVar('space.xs'),
                  borderRadius: cssVar('radius.full'),
                  background: cssVar('color.fg.inverse'),
                }}
              />
              LIVE
            </span>
          ) : (
            <Text step="label" tone="overlay.fg.muted">
              {new Date(show.scheduledStartAt).toLocaleString()}
            </Text>
          )}
        </div>

        <div
          style={{
            position: 'absolute',
            left: cssVar('space.md'),
            right: cssVar('space.md'),
            bottom: cssVar('space.md'),
          }}
        >
          <Stack gap="xs">
            <Text step="heading" tone="overlay.fg" truncate>
              {show.title}
            </Text>
            <Text step="label" tone="overlay.fg.muted">
              {show.sellerDisplayName} · {show.lineupSize} {show.lineupSize === 1 ? 'lot' : 'lots'}
            </Text>
          </Stack>
        </div>
      </Box>
    </button>
  );
}
